// Pure data helpers for the Product Studio edit workspace.
//
// Everything here is presentation logic derived from the real workspace
// payload (GET products/catalog/:id/workspace). Nothing here invents data:
// if the API does not return something, these helpers do not fabricate it.

import {
  coerceNullableNumber,
  createEmptyEnrichmentForm,
  getReferenceOptionLabel,
  mapEnrichmentToForm,
  normalizeMediaRole,
  toInputValue,
} from "../../../utils/catalogProductStudioShared";
import {
  isColorDimensionName,
  mediaCanCoverColor,
  normalizeDimensionKey,
} from "../../../utils/catalogColorCover";
import { isSizeDimensionName, SIZE_PRESETS } from "../wizardCreateHelpers";

// ── Limits mirrored from the API (the API stays the authority) ──────────────
export const MAX_PRODUCT_MEDIA = 30;
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const ALLOWED_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp"];
/** Same threshold as the API's catalog-stock-risk builder. */
const LOW_STOCK_THRESHOLD = 2;
export const MAX_RESTOCK_ROW_QUANTITY = 9999;

export const STOREFRONT_BASE_URL = String(
  process.env.REACT_APP_STOREFRONT_URL || "https://blackandwhiteprishtine.com"
).replace(/\/+$/, "");

export const buildStorefrontProductUrl = (slug) =>
  slug ? `${STOREFRONT_BASE_URL}/shop/${encodeURIComponent(slug)}` : "";

// ── Tabs & deep links ───────────────────────────────────────────────────────
export const STUDIO_TABS = [
  "overview",
  "media",
  "variants",
  "inventory",
  "details",
  "publishing",
  "history",
];

/** Old section hashes keep working so bookmarks and notifications still land. */
const LEGACY_HASHES = {
  story: "details",
  options: "variants",
  stock: "inventory",
};

export const resolveTabFromHash = (hash) => {
  const key = String(hash || "").replace(/^#/, "").trim();
  if (STUDIO_TABS.includes(key)) return key;
  return LEGACY_HASHES[key] || "overview";
};

// ── Formatting ──────────────────────────────────────────────────────────────
export const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `€${parsed.toFixed(2)}` : "—";
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return String(value);
  }
};

export const formatTime = (value) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
};

export const formatDay = (value) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
};

export const fill = (template, values = {}) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.split(`{${key}}`).join(String(value)),
    String(template || "")
  );

// ── Product status ──────────────────────────────────────────────────────────
/**
 * The four real states. "posOnly" is the API's `unpublished`: status ACTIVE
 * without `publishedAt` — sellable at the till but hidden online.
 */
export const getProductStatusKey = (workspace) => {
  const status = workspace?.product?.status;
  if (status === "archived") return "archived";
  if (status === "active") {
    return workspace?.publishing?.publishedAt ? "published" : "posOnly";
  }
  return "draft";
};

export const PRODUCT_STATUS_META = {
  published: { labelKey: "statusPublished", tone: "success" },
  posOnly: { labelKey: "statusPosOnly", tone: "info" },
  draft: { labelKey: "statusDraft", tone: "neutral" },
  archived: { labelKey: "statusArchived", tone: "danger" },
};

/** Status key → the value PUT publishing expects. */
export const STATUS_TO_API = {
  published: "active",
  posOnly: "unpublished",
  draft: "draft",
  archived: "archived",
};

// ── Options, colours, variant labels ────────────────────────────────────────
export const findColorOption = (options = []) =>
  (options || []).find((option) => isColorDimensionName(option?.key, option?.name)) || null;

const getVariantOptionValues = (variant) =>
  (variant?.optionValues || [])
    .map((entry) => String(entry?.value || "").trim())
    .filter(Boolean);

/** "Taba · 38" — the human identity of a variant. Falls back to its title. */
export const getVariantLabel = (variant) => {
  const values = getVariantOptionValues(variant);
  if (values.length) return values.join(" · ");
  return String(variant?.title || "").trim();
};

export const getVariantColorValue = (variant, colorOption) => {
  if (!colorOption) return null;
  const match = (variant?.optionValues || []).find(
    (entry) => entry.optionId === colorOption.id || entry.optionName === colorOption.name
  );
  return match ? { optionValueId: match.optionValueId, value: match.value } : null;
};

/** Value of one option dimension on a variant, by option id or name. */
export const getVariantOptionValue = (variant, option) => {
  const match = (variant?.optionValues || []).find(
    (entry) => entry.optionId === option.id || entry.optionName === option.name
  );
  return match?.value || "";
};

// ── Stock ───────────────────────────────────────────────────────────────────
export const getStockLevel = (available) => {
  const value = Number(available || 0);
  if (value <= 0) return "out";
  if (value <= LOW_STOCK_THRESHOLD) return "low";
  return "ok";
};

const RISK_TO_LEVEL = { OUT_OF_STOCK: "out", LOW_STOCK: "low", OK: "ok" };

/**
 * Per-variant totals across every location. Uses the server summary (which
 * already picks aggregate vs serialized) and only falls back to recomputing
 * when an older payload lacks it.
 */
export const getVariantStock = (variant, inventoryMode = "aggregate") => {
  const summary = variant?.inventory?.summary;
  if (summary) {
    return {
      onHand: Number(summary.onHand || 0),
      reserved: Number(summary.reserved || 0),
      available: Number(summary.available || 0),
      level: RISK_TO_LEVEL[summary.stockRiskLevel] || getStockLevel(summary.available),
    };
  }

  if (inventoryMode === "serialized") {
    const serialized = variant?.inventory?.serialized || {};
    const available = Number(serialized.available || 0);
    return {
      onHand: Number(serialized.total || 0),
      reserved: Number(serialized.reserved || 0),
      available,
      level: getStockLevel(available),
    };
  }

  const rows = variant?.inventory?.aggregateByLocation || [];
  const onHand = rows.reduce((sum, row) => sum + Number(row.onHand || 0), 0);
  const reserved = rows.reduce((sum, row) => sum + Number(row.reserved || 0), 0);
  const available = rows.reduce(
    (sum, row) => sum + Math.max(0, Number(row.onHand || 0) - Number(row.reserved || 0)),
    0
  );
  return { onHand, reserved, available, level: getStockLevel(available) };
};

/** Aggregate stock of one variant at one location (zeros when absent). */
export const getLocationStock = (variant, locationId) => {
  const row = (variant?.inventory?.aggregateByLocation || []).find(
    (entry) => Number(entry.locationId) === Number(locationId)
  );
  const onHand = Number(row?.onHand || 0);
  const reserved = Number(row?.reserved || 0);
  const available = row?.available ?? Math.max(0, onHand - reserved);
  return { onHand, reserved, available: Number(available), level: getStockLevel(available) };
};

export const summarizeStock = (variants = [], inventoryMode = "aggregate") =>
  variants.reduce(
    (summary, variant) => {
      const stock = getVariantStock(variant, inventoryMode);
      summary.onHand += stock.onHand;
      summary.reserved += stock.reserved;
      summary.available += stock.available;
      if (stock.level === "out") summary.out += 1;
      if (stock.level === "low") summary.low += 1;
      return summary;
    },
    { onHand: 0, reserved: 0, available: 0, out: 0, low: 0 }
  );

/**
 * Locations to show as stock columns: every location the company has, plus
 * any location that appears in inventory data but is missing from that list.
 */
export const collectStockLocations = (variants = [], locations = []) => {
  const byId = new Map();
  (locations || []).forEach((location) => {
    byId.set(Number(location.id), { id: Number(location.id), name: location.name });
  });
  variants.forEach((variant) => {
    (variant?.inventory?.aggregateByLocation || []).forEach((row) => {
      const id = Number(row.locationId);
      if (!byId.has(id)) byId.set(id, { id, name: row.locationName || "" });
    });
  });
  return Array.from(byId.values());
};

export const getLocationsWithStock = (variants = []) => {
  const byId = new Map();
  variants.forEach((variant) => {
    (variant?.inventory?.aggregateByLocation || []).forEach((row) => {
      if (Number(row.onHand || 0) > 0 || Number(row.reserved || 0) > 0) {
        byId.set(Number(row.locationId), row.locationName || "");
      }
    });
  });
  return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
};

// ── Media ───────────────────────────────────────────────────────────────────
export const isFilledMedia = (item) => Boolean(item?.storageKey || item?.sourceUrl);

const isCoverItem = (item) =>
  typeof item?.isCover === "boolean" ? item.isCover : Boolean(item?.isPrimary);

export const getCoverImage = (media = []) => {
  const filled = (media || []).filter(isFilledMedia);
  return filled.find(isCoverItem) || filled[0] || null;
};

/**
 * The image that represents a variant: its colour's cover, then the first
 * image assigned to it, then the product cover. Mirrors the storefront order.
 */
export const getVariantImage = (variant, media = [], colorOption = null) => {
  const filled = (media || []).filter(isFilledMedia);
  const color = getVariantColorValue(variant, colorOption);
  if (color) {
    const colorCover = filled.find((item) =>
      (item.colorCoverOptionValueIds || []).includes(color.optionValueId)
    );
    if (colorCover) return colorCover;
  }
  const assigned = filled.find((item) => (item.variantIds || []).includes(variant?.id));
  return assigned || getCoverImage(filled);
};

/**
 * Colour groups for the media tab. An image belongs to a colour when it is
 * assigned to one of that colour's variants or is that colour's cover. Images
 * with no variant assignment are product-wide and go to the shared group.
 * Each entry keeps `index` — its position in the draft list (gallery order).
 */
export const buildMediaGroups = (media = [], colors = []) => {
  const entries = (media || [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isFilledMedia(item));

  if (!colors.length) {
    return [{ key: "all", color: null, entries }];
  }

  const groups = colors.map((color) => {
    const colorVariantIds = new Set((color.variantIds || []).map(Number));
    return {
      key: `color-${color.optionValueId}`,
      color,
      entries: entries.filter(({ item }) => {
        const variantIds = (item.variantIds || []).map(Number);
        return (
          variantIds.some((id) => colorVariantIds.has(id)) ||
          (item.colorCoverOptionValueIds || []).includes(color.optionValueId)
        );
      }),
    };
  });

  const shared = entries.filter(
    ({ item }) =>
      !(item.variantIds || []).length && !(item.colorCoverOptionValueIds || []).length
  );

  return shared.length
    ? [...groups, { key: "shared", color: null, shared: true, entries: shared }]
    : groups;
};

export const getColorCoverEntry = (media = [], color) => {
  if (!color) return null;
  const index = (media || []).findIndex((item) =>
    (item.colorCoverOptionValueIds || []).includes(color.optionValueId)
  );
  return index === -1 ? null : { item: media[index], index };
};

/** Colours that have no image of their own (assigned or cover). */
export const getColorsWithoutPhotos = (media = [], colors = []) =>
  buildMediaGroups(media, colors)
    .filter((group) => group.color && !group.entries.length)
    .map((group) => group.color);

export const getMediaStats = (media = [], colors = []) => {
  const filled = (media || []).filter(isFilledMedia);
  const groups = buildMediaGroups(media, colors).filter((group) => group.color);
  const coveredColorIds = new Set(filled.flatMap((item) => item.colorCoverOptionValueIds || []));
  return {
    total: filled.length,
    withAlt: filled.filter((item) => String(item.altText || "").trim()).length,
    colorsTotal: colors.length,
    colorsWithPhotos: groups.filter((group) => group.entries.length).length,
    colorsWithCover: colors.filter((color) => coveredColorIds.has(color.optionValueId)).length,
    perColor: groups.map((group) => group.entries.length),
  };
};

export { mediaCanCoverColor };

// ── Prices ──────────────────────────────────────────────────────────────────
export const summarizePrices = (variants = []) => {
  const prices = variants
    .map((variant) => coerceNullableNumber(variant.price))
    .filter((value) => value !== undefined);
  if (!prices.length) return { min: null, max: null, same: true, count: 0 };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { min, max, same: min === max && prices.length === variants.length, count: prices.length };
};

// ── Workspace → editable form state ─────────────────────────────────────────
export const createEmptyDimension = (name = "") => ({ name, valuesText: "" });

// ── Option value suggestions (structure editor) ─────────────────────────────
export const classifyDimensionName = (name) => {
  if (isColorDimensionName(name)) return "color";
  if (isSizeDimensionName(name)) return "size";
  return null;
};

const SIZE_SUGGESTIONS_BY_FAMILY = {
  shoes: SIZE_PRESETS.footwear,
  textile: SIZE_PRESETS.apparel,
};

/**
 * Values a merchant can pick for a dimension instead of typing them: the
 * catalog's colours for a colour dimension, the family's size preset for a
 * size dimension. Values the product uses that the list lacks follow it, so
 * picking a value never reorders the buttons.
 */
export const buildDimensionValueSuggestions = ({ name, productFamily, colorRecords = [], currentValues = [] }) => {
  const kind = classifyDimensionName(name);
  const base =
    kind === "color"
      ? colorRecords.map((record) => getReferenceOptionLabel(record, "")).filter(Boolean)
      : kind === "size"
      ? SIZE_SUGGESTIONS_BY_FAMILY[productFamily] || [...SIZE_PRESETS.footwear, ...SIZE_PRESETS.apparel]
      : [];
  if (!base.length) return [];

  const seen = new Set();
  return [...base, ...currentValues].filter((value) => {
    const key = normalizeDimensionKey(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const splitDimensionValues = (valuesText) =>
  String(valuesText || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

/** Adds or removes one value (matched without case/accents), keeping the typed order. */
export const toggleDimensionValue = (valuesText, value) => {
  const values = splitDimensionValues(valuesText);
  const key = normalizeDimensionKey(value);
  const next = values.some((entry) => normalizeDimensionKey(entry) === key)
    ? values.filter((entry) => normalizeDimensionKey(entry) !== key)
    : [...values, value];
  return next.join(", ");
};

export const mapWorkspaceToCoreForm = (workspace) => {
  const product = workspace?.product || {};
  return {
    title: product.title || "",
    slug: product.slug || "",
    description: product.description || "",
    productFamily: product.productFamily || "shoes",
    brandId: product.brandId === null || product.brandId === undefined ? "" : String(product.brandId),
    merchandisingTypeId:
      product.merchandisingTypeId === null || product.merchandisingTypeId === undefined
        ? ""
        : String(product.merchandisingTypeId),
    baseSku: product.baseSku || "",
    metaCatalogEnabled: Boolean(product.metaCatalogEnabled),
    enrichment: product.enrichment ? mapEnrichmentToForm(product.enrichment) : createEmptyEnrichmentForm(),
  };
};

/**
 * The shared-pricing baseline is the first live variant (archived variants
 * never receive shared prices, so they must not seed the field either).
 */
export const mapWorkspaceToStructureForm = (workspace) => {
  const variants = workspace?.variants || [];
  const options = workspace?.options || [];
  const product = workspace?.product || {};
  const pricingSource = variants.find((variant) => variant.status !== "archived") || variants[0];

  return {
    productMode: product.productMode || "simple",
    generationMode: product.generationMode || "manual",
    inventoryMode: product.inventoryMode || "aggregate",
    price: toInputValue(pricingSource?.price),
    compareAtPrice: toInputValue(pricingSource?.compareAtPrice),
    cost: toInputValue(pricingSource?.cost),
    optionDimensions: options.length
      ? options.map((option) => ({
          name: option.name,
          valuesText: option.values.map((value) => value.value).join(", "),
        }))
      : [createEmptyDimension()],
    manualCombinations:
      product.productMode === "variant" && product.generationMode === "manual"
        ? variants
            .filter((variant) => variant.status !== "archived")
            .map((variant) => ({
              optionValues: options.map((option) => {
                const match = variant.optionValues.find(
                  (optionValue) => optionValue.optionName === option.name
                );
                return match?.value || "";
              }),
            }))
        : [],
  };
};

export const mapWorkspaceToVariantDrafts = (workspace) =>
  (workspace?.variants || []).map((variant) => ({
    id: variant.id,
    title: variant.title,
    sku: variant.sku || "",
    barcode: variant.barcode || "",
    status: variant.status,
    position: variant.position,
    isDefault: variant.isDefault,
    price: toInputValue(variant.price),
    compareAtPrice: toInputValue(variant.compareAtPrice),
    cost: toInputValue(variant.cost),
  }));

export const mapWorkspaceToMediaDrafts = (workspace) =>
  (workspace?.media || []).map((item, itemIndex) => ({
    id: item.id,
    storageKey: item.storageKey || "",
    sourceUrl: item.sourceUrl || "",
    originalName: item.storageKey ? String(item.storageKey).split("/").pop() || "" : "",
    altText: item.altText || "",
    role: normalizeMediaRole(item.role),
    variantIds: item.variantIds || [],
    thumbnailKey: item.thumbnailKey || "",
    cardKey: item.cardKey || "",
    fullKey: item.fullKey || "",
    isCover: typeof item.isPrimary === "boolean" ? item.isPrimary : itemIndex === 0,
    colorCoverOptionValueIds: item.colorCoverOptionValueIds || [],
  }));

// ── Form state → API payloads (unchanged contracts) ─────────────────────────
export const buildCorePayload = (coreForm, enrichmentPayload) => ({
  title: coreForm.title,
  slug: coreForm.slug || undefined,
  description: coreForm.description || undefined,
  productFamily: coreForm.productFamily,
  brandId: coreForm.brandId ? Number(coreForm.brandId) : undefined,
  merchandisingTypeId: coreForm.merchandisingTypeId ? Number(coreForm.merchandisingTypeId) : undefined,
  baseSku: coreForm.baseSku || undefined,
  metaCatalogEnabled: coreForm.metaCatalogEnabled,
  enrichment: enrichmentPayload,
});

export const mapVariantDraftsToSaveItems = (drafts) =>
  drafts.map((variant) => ({
    id: variant.id,
    title: variant.title,
    sku: variant.sku,
    barcode: variant.barcode || null,
    status: variant.status,
    position: Number(variant.position) || 1,
    isDefault: Boolean(variant.isDefault),
    price: coerceNullableNumber(variant.price),
    compareAtPrice: coerceNullableNumber(variant.compareAtPrice),
    cost: coerceNullableNumber(variant.cost),
  }));

export const variantDraftHasUnsavedChanges = (draft, source) => {
  if (!source) return false;
  return (
    draft.sku !== (source.sku || "") ||
    draft.barcode !== (source.barcode || "") ||
    draft.price !== toInputValue(source.price) ||
    draft.compareAtPrice !== toInputValue(source.compareAtPrice) ||
    draft.cost !== toInputValue(source.cost) ||
    draft.status !== source.status ||
    Boolean(draft.isDefault) !== Boolean(source.isDefault) ||
    Number(draft.position) !== Number(source.position)
  );
};

export const mapMediaDraftsToSaveItems = (drafts) =>
  drafts
    .map((item, index) => ({
      storageKey: item.storageKey || undefined,
      sourceUrl: !item.storageKey && item.sourceUrl ? item.sourceUrl : undefined,
      altText: item.altText || undefined,
      role: normalizeMediaRole(item.role),
      isPrimary: Boolean(item.isCover),
      position: index + 1,
      variantIds: item.variantIds || [],
      colorCoverOptionValueIds: item.colorCoverOptionValueIds || [],
      thumbnailKey: item.thumbnailKey || undefined,
      cardKey: item.cardKey || undefined,
      fullKey: item.fullKey || undefined,
    }))
    .filter((item) => item.storageKey || item.sourceUrl);

export const normalizeOptionDimensions = (dimensions) =>
  (dimensions || [])
    .map((dimension) => ({
      name: String(dimension?.name || "").trim(),
      values: String(dimension?.valuesText || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    }))
    .filter((dimension) => dimension.name && dimension.values.length);

const buildManualCombinationsPayload = (dimensions, manualCombinations) => {
  const normalizedDimensions = normalizeOptionDimensions(dimensions);
  const dimensionCount = normalizedDimensions.length;
  if (!dimensionCount) return undefined;

  const combinations = (manualCombinations || [])
    .map((combination) => ({
      optionValues: normalizedDimensions.map((_dimension, index) =>
        String(combination?.optionValues?.[index] || "").trim()
      ),
    }))
    .filter(
      (combination) =>
        combination.optionValues.length === dimensionCount &&
        combination.optionValues.every(Boolean)
    );

  return combinations.length ? combinations : undefined;
};

export const buildStructurePayload = (structureForm) => {
  const productMode = structureForm.productMode;
  return {
    productMode,
    generationMode: productMode === "variant" ? structureForm.generationMode : "manual",
    optionDimensions:
      productMode === "variant" ? normalizeOptionDimensions(structureForm.optionDimensions) : undefined,
    manualCombinations:
      productMode === "variant" && structureForm.generationMode === "manual"
        ? buildManualCombinationsPayload(structureForm.optionDimensions, structureForm.manualCombinations)
        : undefined,
    pricing: {
      price: coerceNullableNumber(structureForm.price),
      compareAtPrice: coerceNullableNumber(structureForm.compareAtPrice),
      cost: coerceNullableNumber(structureForm.cost),
    },
    inventory: { mode: structureForm.inventoryMode },
  };
};

/** Only the fields that define the variant structure (not pricing). */
export const getStructureSnapshot = (form) =>
  JSON.stringify({
    productMode: form?.productMode,
    generationMode: form?.generationMode,
    inventoryMode: form?.inventoryMode,
    dimensions: normalizeOptionDimensions(form?.optionDimensions),
    manual:
      form?.productMode === "variant" && form?.generationMode === "manual"
        ? buildManualCombinationsPayload(form?.optionDimensions, form?.manualCombinations) || []
        : [],
  });

// ── Structure change estimate (shown before a destructive save) ────────────
const cartesian = (lists) =>
  lists.reduce(
    (acc, list) => acc.flatMap((prefix) => list.map((value) => [...prefix, value])),
    [[]]
  );

/** Order-independent combination key: "madhesia=38|ngjyra=taba". */
const pairsKey = (pairs) =>
  pairs
    .map(([name, value]) => `${normalizeDimensionKey(name)}=${normalizeDimensionKey(value)}`)
    .sort()
    .join("|");

const variantCombinationKey = (variant) =>
  pairsKey((variant.optionValues || []).map((entry) => [entry.optionName, entry.value]));

/** Combination keys a structure form would produce (mirrors the API plan). */
const plannedCombinationKeys = (form) => {
  if (form?.productMode !== "variant") return new Set([pairsKey([])]);
  const dimensions = normalizeOptionDimensions(form.optionDimensions);
  if (!dimensions.length) return new Set();
  const tuples =
    form.generationMode === "manual"
      ? (buildManualCombinationsPayload(form.optionDimensions, form.manualCombinations) || []).map(
          (combination) => combination.optionValues
        )
      : cartesian(dimensions.map((dimension) => dimension.values));
  return new Set(
    tuples.map((values) => pairsKey(values.map((value, index) => [dimensions[index].name, value])))
  );
};

/**
 * Estimates what saving `structureForm` would do to the existing variants.
 * Mirrors the API's rules: a planned combination that matches a live variant
 * is kept; one that matches an archived variant is restored only if it is
 * newly reintroduced (not part of the currently saved structure) — otherwise
 * it stays archived; anything else is created. Live variants the plan no
 * longer contains are archived. The server's own summary is shown after saving.
 */
export const estimateStructureChange = (structureForm, workspace) => {
  const variants = workspace?.variants || [];
  const live = variants.filter((variant) => variant.status !== "archived");
  const archived = variants.filter((variant) => variant.status === "archived");

  const plannedKeys = plannedCombinationKeys(structureForm);
  const savedForm = workspace?.product ? mapWorkspaceToStructureForm(workspace) : null;
  const previousKeys =
    savedForm?.productMode === "variant"
      ? plannedCombinationKeys(savedForm)
      : new Set(live.map(variantCombinationKey));
  const liveKeys = new Set(live.map(variantCombinationKey));
  const archivedKeys = new Set(archived.map(variantCombinationKey));

  let kept = 0;
  let restored = 0;
  let created = 0;
  let staysArchived = 0;
  plannedKeys.forEach((key) => {
    if (liveKeys.has(key)) kept += 1;
    else if (archivedKeys.has(key)) {
      if (previousKeys.has(key)) staysArchived += 1;
      else restored += 1;
    } else created += 1;
  });
  const archivedCount = live.filter((variant) => !plannedKeys.has(variantCombinationKey(variant))).length;

  return { planned: plannedKeys.size, kept, restored, created, staysArchived, archived: archivedCount };
};

// ── Publishing readiness ────────────────────────────────────────────────────
const BLOCKING_ISSUE_KEYS = [
  [/title is required/i, "biTitle"],
  [/family is required/i, "biFamily"],
  [/non-archived variant/i, "biNoVariant"],
  [/simple products must/i, "biSimple"],
  [/default variant/i, "biDefault"],
  [/must have a sku/i, "biSku"],
  [/must have a price/i, "biPrice"],
];

/** Server blocking issue (English sentence) → Albanian copy key, or null. */
export const getBlockingIssueKey = (issue) => {
  const match = BLOCKING_ISSUE_KEYS.find(([pattern]) => pattern.test(String(issue || "")));
  return match ? match[1] : null;
};

export const translateBlockingIssue = (issue, tt) => {
  const key = getBlockingIssueKey(issue);
  return key ? tt(key) : String(issue || "");
};

/**
 * Non-blocking recommendations, derived from the current (possibly unsaved)
 * drafts. The API never blocks on these — they are advice only.
 */
export const buildRecommendations = ({ coreForm, media, colors, liveVariants, inventoryMode }) => {
  const recommendations = [];
  const filled = (media || []).filter(isFilledMedia);
  if (!filled.length) recommendations.push({ key: "pbRecNoPhotos", tab: "media" });
  if (!String(coreForm?.description || "").trim()) {
    recommendations.push({ key: "pbRecNoDescription", tab: "overview" });
  }
  if (!coreForm?.brandId) recommendations.push({ key: "pbRecNoBrand", tab: "overview" });
  if (liveVariants?.length && summarizeStock(liveVariants, inventoryMode).available <= 0) {
    recommendations.push({ key: "pbRecNoStock", tab: "inventory" });
  }
  const missingAlt = filled.filter((item) => !String(item.altText || "").trim()).length;
  if (missingAlt) recommendations.push({ key: "pbRecMissingAlt", tab: "media", values: { count: missingAlt } });
  const colorsWithout = getColorsWithoutPhotos(media, colors);
  if (filled.length && colorsWithout.length) {
    recommendations.push({
      key: "pbRecColorPhotos",
      tab: "media",
      values: { colors: colorsWithout.map((color) => color.value).join(", ") },
    });
  }
  return recommendations;
};

// ── Inventory movements ─────────────────────────────────────────────────────
const MOVEMENT_KEYS = {
  restock: "mvRestock",
  undo_receive: "mvUndoReceive",
  manual_adjustment: "mvManualAdjustment",
  correction: "mvCorrection",
  import: "mvImport",
  sale: "mvSale",
  return: "mvReturn",
  transfer: "mvTransfer",
  serialized_unit_create: "mvSerializedUnitCreate",
};

export const getMovementLabelKey = (type) =>
  MOVEMENT_KEYS[String(type || "").toLowerCase()] || "mvOther";

export const getMovementTone = (type, delta) => {
  const key = String(type || "").toLowerCase();
  if (key === "sale") return "sale";
  if (key === "return") return "return";
  if (key === "correction" || key === "manual_adjustment") return "adjust";
  if (key === "undo_receive") return "undo";
  return Number(delta) >= 0 ? "in" : "out";
};

const REFERENCE_RULES = [
  [/^sale:(\d+)/, "refSale"],
  [/^order-edit:(\d+)/, "refOrderEdit"],
  [/^order(?:-create)?:(\d+)/, "refOrder"],
  [/^return:(\d+)/, "refReturn"],
  [/^exchange:(\d+)/, "refExchange"],
  [/^catalog-movement:(\d+):undo/, "refUndo"],
];

/**
 * Movement source reference ("sale:36:line:44") → readable text ("Shitja #36").
 * The product's own adjustment reference adds nothing and is hidden (null);
 * unknown formats are shown unchanged.
 */
export const humanizeMovementReference = (reference, tt) => {
  const value = String(reference || "").trim();
  if (!value || /^catalog-product:\d+:adjustment$/.test(value)) return null;
  for (const [pattern, key] of REFERENCE_RULES) {
    const match = value.match(pattern);
    if (match) return fill(tt(key), { id: match[1] });
  }
  return value;
};

/** Sentence key for a movement, so a sale never reads as a manual removal. */
export const getMovementSentenceKey = (type, delta) => {
  const key = String(type || "").toLowerCase();
  if (key === "sale") return Number(delta) < 0 ? "mvSentenceSale" : "mvSentenceSaleCancel";
  if (key === "return") return Number(delta) >= 0 ? "mvSentenceReturn" : "mvSentenceOut";
  return Number(delta) >= 0 ? "mvSentenceIn" : "mvSentenceOut";
};

/** Groups movements (already newest-first) by calendar day. */
export const groupMovementsByDay = (items = []) => {
  const groups = [];
  const byKey = new Map();
  items.forEach((item) => {
    const date = new Date(item.createdAt);
    const key = Number.isNaN(date.getTime())
      ? "unknown"
      : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    if (!byKey.has(key)) {
      const group = { key, date: item.createdAt, items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).items.push(item);
  });
  return groups;
};

export const getRelativeDayKey = (value, now = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((startOf(now) - startOf(date)) / 86400000);
  if (diff === 0) return "hsToday";
  if (diff === 1) return "hsYesterday";
  return null;
};

// ── Errors (backend English → Albanian) ─────────────────────────────────────
const ERROR_RULES = [
  [/catalog product was not found/i, "errNotFound"],
  [/variant \d+ (was not found|does not belong)/i, "errVariantMissing"],
  [/location was not found/i, "errLocationMissing"],
  [/brand was not found/i, "errBrandMissing"],
  [/merchandising type was not found/i, "errTypeMissing"],
  [/may not have more than (\d+) media/i, "errMediaLimit"],
  [/must include a storageKey or sourceUrl/i, "errMediaIncomplete"],
  [/is not a colour of this product/i, "errColorGone"],
  [/already has a colour cover/i, "errColorCoverTaken"],
  [/cannot be that colour/i, "errColorCoverIneligible"],
  [/only one media item can be marked as primary/i, "errOnePrimary"],
  [/only one variant can be marked as default/i, "errOneDefault"],
  [/duplicate barcode "([^"]*)"/i, "errBarcodeDuplicate"],
  [/barcode "([^"]*)" is already used/i, "errBarcodeTaken"],
  [/a product with the code "([^"]*)" already exists/i, "errCodeTaken"],
  [/variant sku cannot be empty/i, "errSkuEmpty"],
  [/barcode cannot be empty/i, "errBarcodeEmpty"],
  [/does not change stock/i, "errNoChange"],
  [/cannot be negative/i, "errNegative"],
  [/lower than reserved/i, "errBelowReserved"],
  [/supported only for aggregate/i, "errSerializedAdjust"],
  [/an image file is required/i, "errFileRequired"],
  [/file is too large/i, "errFileTooLarge"],
  [/unsupported file type/i, "errFileType"],
  [/does not match a supported image format/i, "errFileCorrupt"],
];

const extractRawMessage = (error) => {
  const data = error?.data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.message?.message === "string") return data.message.message;
  if (Array.isArray(data?.message)) return data.message.join(" ");
  if (typeof data?.error === "string" && data.error !== "Bad Request") return data.error;
  if (typeof error?.message === "string") return error.message;
  return "";
};

const getBlockingIssuesFromError = (error) => {
  const fromDetails = error?.data?.details?.blockingIssues;
  if (Array.isArray(fromDetails)) return fromDetails;
  const nested = error?.data?.message?.details?.blockingIssues || error?.data?.message?.blockingIssues;
  return Array.isArray(nested) ? nested : [];
};

/**
 * Turns an RTK Query error into clear Albanian (or English) copy. Known server
 * messages are translated; anything else falls back to the section message.
 * The raw server text is never shown on its own.
 */
export const humanizeCatalogError = (error, tt, fallbackKey = "errGeneric") => {
  const status = error?.status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") return tt("errNetwork");
  if (status === 401) return tt("errSession");
  if (status === 403) return tt("errPermission");

  const blockingIssues = getBlockingIssuesFromError(error);
  if (blockingIssues.length) {
    return `${tt("errNotReady")} ${blockingIssues.map((issue) => translateBlockingIssue(issue, tt)).join(" ")}`;
  }

  const raw = extractRawMessage(error);
  for (const [pattern, key] of ERROR_RULES) {
    const match = raw.match(pattern);
    if (match) {
      if (key === "errMediaLimit") return fill(tt(key), { max: match[1] || MAX_PRODUCT_MEDIA });
      if (key === "errBarcodeDuplicate" || key === "errBarcodeTaken" || key === "errCodeTaken") {
        return fill(tt(key), { code: match[1] || "" });
      }
      return tt(key);
    }
  }

  if (status === 404) return tt("errNotFound");
  return tt(fallbackKey);
};
